/**
 * ToteManualEntryPage — Full-screen manual tote barcode entry page,
 * consistent with ManualEntryPage (product flow).
 */

import React, { useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Icon } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import "./classification.scss";
import "../landing/landing.scss";
import PillButton from "src/components/UI/PillButton";
import useKeyboardScrollReset from "src/hooks/useKeyboardScrollReset";

interface ToteManualEntryPageProps {
  onSwitchToScanner: () => void;
  onSubmit: (barcode: string) => void;
}

const ToteManualEntryPage: React.FC<ToteManualEntryPageProps> = ({
  onSwitchToScanner,
  onSubmit,
}) => {
  const [barcode, setBarcode] = useState("");
  const [error, setError] = useState<string | null>(null);
  useKeyboardScrollReset();

  const handleSubmit = () => {
    const trimmed = barcode.trim();
    if (!trimmed) {
      setError("Please enter a tote barcode.");
      return;
    }
    setError(null);
    onSubmit(trimmed);
  };

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacingInset="400">
      {/* Back arrow */}
      <Row spacingInset="400">
        <Button type="icon" onClick={onSwitchToScanner}>
          <Icon tokens={arrowLeftTokens} />
        </Button>
      </Row>

      {/* Card body */}
      <Column className="home-card" spacing="500" spacingInset="500" alignmentHorizontal="center">
        <Column spacing="400" alignmentHorizontal="center">
          <Text type="h500">Manual Entry</Text>
          <Text color="secondary" type="b300" alignment="center">
            Enter the tote barcode to load its contents
          </Text>
        </Column>

        <Column spacing="500" width="100%">
          <Column spacing="200">
            <Row alignmentVertical="center" spacing="200">
              <Text>Tote Barcode</Text>
            </Row>
            <Input
              type="text"
              placeholder="Enter tote barcode"
              value={barcode}
              onChange={(value: string) => {
                setBarcode(value);
                setError(null);
              }}
            />
          </Column>
        </Column>

        {/* Error message */}
        {error && (
          <Text color="error" type="b400">
            {error}
          </Text>
        )}

        {/* Submit button */}
        <PillButton text="LOAD TOTE" onClick={handleSubmit} disabled={!barcode.trim()} />
        <Button type="link" onClick={onSwitchToScanner}>
          SWITCH TO BARCODE SCANNER
        </Button>
      </Column>
    </Column>
  );
};

export default ToteManualEntryPage;
